import { CONTACT_STATUSES, type ContactStatus } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { Bdi, Ltr } from "../../components/Bdi.js";
import { useT } from "../../i18n/LocaleProvider.js";
import { api } from "../../lib/api.js";

interface Contact {
  id: string;
  topic: string;
  name: string;
  email: string;
  phone: string | null;
  languages: string[] | null;
  location: string | null;
  links: string[] | null;
  message: string;
  keep_on_file: boolean;
  locale: string;
  status: ContactStatus;
  spam_flagged: boolean;
  created_at: string;
}
interface Supporter {
  id: string;
  email: string;
  name: string | null;
  help_funding: boolean;
  help_screening: boolean;
  help_connect: boolean;
  help_volunteer: boolean;
  amount_intent: string | null;
  message: string | null;
  consent_updates: boolean;
  locale: string;
  spam_flagged: boolean;
  created_at: string;
}
interface Subscriber {
  id: string;
  email: string;
  locale: string;
  source: string;
  confirmed_at: string | null;
  unsubscribed_at: string | null;
  created_at: string;
}

export function AdminInbox() {
  const { m } = useT();
  const [tab, setTab] = useState<"contacts" | "support" | "subscribers">("contacts");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);

  const load = () => {
    api.get<{ contacts: Contact[] }>("/api/admin/contacts").then((r) => setContacts(r.contacts)).catch(() => {});
    api.get<{ supporters: Supporter[] }>("/api/admin/support").then((r) => setSupporters(r.supporters)).catch(() => {});
    api.get<{ subscribers: Subscriber[] }>("/api/admin/subscribers").then((r) => setSubscribers(r.subscribers)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <>
      <div className="admin__tabs" role="tablist">
        {(["contacts", "support", "subscribers"] as const).map((t) => (
          <button key={t} type="button" className="admin__tab" aria-pressed={tab === t} onClick={() => setTab(t)}>
            {m.admin[t]} ({t === "contacts" ? contacts.length : t === "support" ? supporters.length : subscribers.length})
          </button>
        ))}
      </div>

      {tab === "contacts" && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{m.contact.topicLabel}</th>
                <th>{m.contact.nameLabel}</th>
                <th>{m.contact.messageLabel}</th>
                <th>{m.admin.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td>
                    {m.contact.topics[c.topic as keyof typeof m.contact.topics] ?? c.topic}
                    {c.spam_flagged && <div className="badge badge--status">{m.admin.spamFlag}</div>}
                    {c.keep_on_file && <div className="badge badge--ok">on file</div>}
                  </td>
                  <td>
                    <Bdi>{c.name}</Bdi>
                    <div className="small">
                      <a href={`mailto:${c.email}`}>
                        <Ltr>{c.email}</Ltr>
                      </a>
                    </div>
                    {c.phone && <div className="small"><Ltr>{c.phone}</Ltr></div>}
                    {c.location && <div className="small muted"><Bdi>{c.location}</Bdi></div>}
                    {c.languages && <div className="small muted">{c.languages.join(", ")}</div>}
                    {c.links?.map((l) => (
                      <div key={l} className="small">
                        <a href={l} target="_blank" rel="noopener noreferrer"><Ltr>{l}</Ltr></a>
                      </div>
                    ))}
                  </td>
                  <td style={{ maxWidth: "36ch", whiteSpace: "pre-wrap" }} dir="auto">
                    {c.message}
                  </td>
                  <td>
                    <select className="select" value={c.status} onChange={(e) => api.patch(`/api/admin/contacts/${c.id}`, { status: e.target.value }).then(load)}>
                      {CONTACT_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="muted small">
                    {new Date(c.created_at).toLocaleString()}
                    <div>
                      <button type="button" className="linkish" onClick={() => { if (window.confirm(m.admin.delete)) void api.delete(`/api/admin/contacts/${c.id}`).then(load); }}>
                        {m.admin.delete}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {contacts.length === 0 && <p className="muted">{m.admin.empty}</p>}
        </div>
      )}

      {tab === "support" && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{m.support.emailLabel}</th>
                <th>{m.support.helpLabel}</th>
                <th>{m.support.amountLabel}</th>
                <th>{m.support.messageLabel}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {supporters.map((s) => (
                <tr key={s.id}>
                  <td>
                    <a href={`mailto:${s.email}`}><Ltr>{s.email}</Ltr></a>
                    {s.name && <div className="small"><Bdi>{s.name}</Bdi></div>}
                    {s.spam_flagged && <div className="badge badge--status">{m.admin.spamFlag}</div>}
                  </td>
                  <td className="small">
                    {[s.help_funding && m.support.help.funding, s.help_screening && m.support.help.screening, s.help_connect && m.support.help.connect, s.help_volunteer && m.support.help.volunteer].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td>{s.amount_intent ? m.support.amounts[s.amount_intent as keyof typeof m.support.amounts] : "—"}</td>
                  <td style={{ maxWidth: "30ch", whiteSpace: "pre-wrap" }} dir="auto">{s.message ?? ""}</td>
                  <td className="muted small">{new Date(s.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {supporters.length === 0 && <p className="muted">{m.admin.empty}</p>}
        </div>
      )}

      {tab === "subscribers" && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{m.support.emailLabel}</th>
                <th>{m.nav.language}</th>
                <th>source</th>
                <th>confirmed</th>
                <th>unsubscribed</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => (
                <tr key={s.id}>
                  <td><Ltr>{s.email}</Ltr></td>
                  <td>{s.locale}</td>
                  <td>{s.source}</td>
                  <td className="small">{s.confirmed_at ? new Date(s.confirmed_at).toLocaleDateString() : <span className="badge badge--warn">pending</span>}</td>
                  <td className="small">{s.unsubscribed_at ? new Date(s.unsubscribed_at).toLocaleDateString() : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {subscribers.length === 0 && <p className="muted">{m.admin.empty}</p>}
        </div>
      )}
    </>
  );
}
